/**
 * Surfaces: Card, Dialog, Menu, Tooltip, DefinitionList (prompt.md §25, §37).
 *
 * WHY `Dialog` renders its own overlay instead of using a portal: the web app and
 * the Tauri shell both mount the application shell at the document root, so a
 * `position: fixed` overlay with `aria-modal` behaves identically in both, while
 * a portal would need a DOM global that this package deliberately does not
 * depend on. Escape and focus restoration are handled with React events and an
 * effect, so no global DOM listener is attached.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import { Z_INDEX } from "@dayvise/design-tokens";

import { cardStyle, controlStyle, MUTED_TEXT, overlayStyle, rowStyle, stackStyle } from "../styles.ts";
import { IconButton } from "./Button.tsx";

/** Minimal structural view of "something that can take focus". */
interface FocusableLike {
  focus(): void;
}

function currentlyFocused(): FocusableLike | null {
  const active = (globalThis as { document?: { activeElement?: unknown } }).document?.activeElement;
  return typeof (active as FocusableLike | null)?.focus === "function" ? (active as FocusableLike) : null;
}

export interface CardProps {
  readonly title?: string;
  readonly subtitle?: string;
  readonly footer?: ReactNode;
  readonly elevated?: boolean;
  readonly padded?: boolean;
  readonly children: ReactNode;
}

export function Card({ title, subtitle, footer, elevated = false, padded = true, children }: CardProps) {
  return (
    <section style={cardStyle({ elevated, padded })}>
      {title === undefined && subtitle === undefined ? null : (
        <header style={{ marginBottom: "var(--dayvise-space-md)" }}>
          {title === undefined ? null : (
            <h2 style={{ margin: 0, fontFamily: "var(--dayvise-font-family-sans)", fontSize: "var(--dayvise-font-size-lg)", fontWeight: "var(--dayvise-font-weight-semibold)" }}>{title}</h2>
          )}
          {subtitle === undefined ? null : <p style={{ ...MUTED_TEXT, margin: "var(--dayvise-space-xxs) 0 0" }}>{subtitle}</p>}
        </header>
      )}
      {children}
      {footer === undefined ? null : <footer style={{ marginTop: "var(--dayvise-space-md)" }}>{footer}</footer>}
    </section>
  );
}

export interface DialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly description?: string;
  readonly footer?: ReactNode;
  readonly onClose: () => void;
  readonly children?: ReactNode;
}

export function Dialog({ open, title, description, footer, onClose, children }: DialogProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<FocusableLike | null>(null);

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = currentlyFocused();
    panelRef.current?.focus();
    return () => previouslyFocused.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      style={overlayStyle()}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          onClose();
          return;
        }
        const panel = panelRef.current;
        if (event.key !== "Tab" || !panel) return;
        const focusable = [...panel.querySelectorAll<HTMLElement>("button, a[href], input, select, textarea, [tabindex]")]
          .filter((element) => {
            const style = element.ownerDocument.defaultView?.getComputedStyle(element);
            return element.tabIndex >= 0 && !element.matches(":disabled")
              && !element.closest("[hidden], [aria-hidden='true']")
              && style?.display !== "none" && style?.visibility !== "hidden";
          });
        const first = focusable[0];
        const last = focusable.at(-1);
        const active = panel.ownerDocument.activeElement;
        if (!first || !last) {
          event.preventDefault();
          panel.focus();
        } else if (event.shiftKey && (active === first || active === panel || !panel.contains(active))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || active === panel || !panel.contains(active))) {
          event.preventDefault();
          first.focus();
        }
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{ ...cardStyle({ elevated: true }), width: "min(640px, 100%)", maxHeight: "85vh", overflowY: "auto", outline: "none", zIndex: Z_INDEX.modal + 1 }}
      >
        <header style={rowStyle("var(--dayvise-space-sm)", "space-between")}>
          <h2 id={titleId} style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)", fontWeight: "var(--dayvise-font-weight-semibold)" }}>
            {title}
          </h2>
          <IconButton label="Close" onClick={onClose}>
            <span aria-hidden="true">✕</span>
          </IconButton>
        </header>
        {description === undefined ? null : <p style={{ ...MUTED_TEXT, marginTop: "var(--dayvise-space-xs)" }}>{description}</p>}
        <div style={{ marginTop: "var(--dayvise-space-md)" }}>{children}</div>
        {footer === undefined ? null : <footer style={{ ...rowStyle("var(--dayvise-space-sm)", "flex-end"), marginTop: "var(--dayvise-space-lg)" }}>{footer}</footer>}
      </div>
    </div>
  );
}

export interface MenuItem {
  readonly id: string;
  readonly label: string;
  readonly onSelect: () => void;
  readonly danger?: boolean;
  readonly disabled?: boolean;
}

export interface MenuProps {
  /** Rendered as the trigger; the menu owns the open/closed state. */
  readonly trigger: ReactNode;
  readonly items: readonly MenuItem[];
  readonly label: string;
}

export function Menu({ trigger, items, label }: MenuProps) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const toggle = (): void => setOpen((value) => !value);

  return (
    <div
      style={{ position: "relative", display: "inline-block" }}
      onKeyDown={(event) => {
        if (event.key === "Escape") setOpen(false);
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          toggle();
        }
      }}
    >
      <div role="button" tabIndex={0} aria-haspopup="menu" aria-expanded={open} aria-controls={menuId} onClick={toggle} style={{ display: "inline-flex", cursor: "pointer" }}>
        {trigger}
      </div>
      {open ? (
        <div
          id={menuId}
          role="menu"
          aria-label={label}
          style={{
            position: "absolute",
            top: "calc(100% + var(--dayvise-space-xxs))",
            left: 0,
            minWidth: "200px",
            ...cardStyle({ elevated: true, padded: false }),
            padding: "var(--dayvise-space-xxs)",
            zIndex: Z_INDEX.dropdown,
          }}
        >
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              style={{
                ...controlStyle({ variant: "ghost", size: "md" }),
                justifyContent: "flex-start",
                width: "100%",
                color: item.danger ? "var(--dayvise-color-danger)" : "var(--dayvise-color-text-primary)",
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export interface TooltipProps {
  readonly label: string;
  readonly children: ReactNode;
}

/**
 * A tooltip that degrades gracefully: the label is always exposed through
 * `title` (so keyboard and touch users are not excluded) and additionally shown
 * as a bubble on hover/focus.
 */
export function Tooltip({ label, children }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const id = useId();
  return (
    <span
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
      aria-describedby={id}
      title={label}
    >
      {children}
      {visible ? (
        <span
          id={id}
          role="tooltip"
          style={{
            position: "absolute",
            bottom: "calc(100% + var(--dayvise-space-xxs))",
            left: "50%",
            transform: "translateX(-50%)",
            whiteSpace: "nowrap",
            padding: "var(--dayvise-space-xxs) var(--dayvise-space-sm)",
            borderRadius: "var(--dayvise-radius-sm)",
            background: "var(--dayvise-color-text-primary)",
            color: "var(--dayvise-color-canvas)",
            fontSize: "var(--dayvise-font-size-xs)",
            boxShadow: "var(--dayvise-elevation-medium-shadow)",
            zIndex: Z_INDEX.tooltip,
          }}
        >
          {label}
        </span>
      ) : null}
    </span>
  );
}

/** Inline label/value list used by approval prompts and settings summaries. */
export function DefinitionList({ entries }: { readonly entries: readonly { readonly label: string; readonly value: ReactNode }[] }) {
  return (
    <dl style={{ ...stackStyle("var(--dayvise-space-xs)"), margin: 0 }}>
      {entries.map((entry) => (
        <div key={entry.label} style={{ display: "grid", gridTemplateColumns: "minmax(120px, 30%) 1fr", gap: "var(--dayvise-space-sm)" }}>
          <dt style={{ ...MUTED_TEXT, margin: 0 }}>{entry.label}</dt>
          <dd style={{ margin: 0, overflowWrap: "anywhere", fontSize: "var(--dayvise-font-size-sm)" }}>{entry.value}</dd>
        </div>
      ))}
    </dl>
  );
}
